import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from '../../../i18n/useTranslation';
import { StudentService } from '../../../Services/StudentService';
import {
  GoogleContactsService,
  type GoogleContact
} from '../../../Services/GoogleContactsService';
import { Modal } from '../../SharedArea/Modal/Modal';
import { Spinner } from '../../SharedArea/Spinner/Spinner';
import { EmptyState } from '../../SharedArea/EmptyState/EmptyState';
import { ErrorState } from '../../SharedArea/ErrorState/ErrorState';
import './GoogleContactPicker.css';

/**
 * Pick people out of the tutor's own address book and attach them to a
 * student.
 *
 * Everything ticked is saved in one request, and each carries its Google id,
 * so importing the same person twice updates the contact rather than creating
 * a second one.
 */
export function GoogleContactPicker({
  student, onClose, onImported
}: { student: string; onClose: () => void; onImported: () => void }) {
  const { tr } = useTranslation();
  const [contacts, setContacts] = useState<GoogleContact[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    GoogleContactsService.list()
      .then(setContacts)
      .catch(() => setError('SERVER_ERROR'));
    return () => GoogleContactsService.clearCache();
  }, []);

  const visible = useMemo(() => {
    if (!contacts) return [];
    const needle = query.trim().toLowerCase();
    if (!needle) return contacts.slice(0, 100);
    return contacts
      .filter(contact =>
        contact.name.toLowerCase().includes(needle) || contact.phone.includes(needle))
      .slice(0, 100);
  }, [contacts, query]);

  function toggle(resourceName: string) {
    setPicked(current => {
      const next = new Set(current);
      if (next.has(resourceName)) next.delete(resourceName);
      else next.add(resourceName);
      return next;
    });
  }

  async function importPicked() {
    if (!contacts || picked.size === 0) return;
    setBusy(true);
    try {
      await StudentService.addContacts(
        student,
        contacts
          .filter(contact => picked.has(contact.resourceName))
          .map(contact => ({
            name: contact.name,
            phone: contact.phone,
            googleResourceName: contact.resourceName,
            ...(contact.relation ? { relation: contact.relation } : {})
          }))
      );
      onImported();
    } catch {
      setError('SERVER_ERROR');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      title={tr('importFromGoogle')}
      subtitle={student}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="ghost-button" onClick={onClose}>{tr('cancel')}</button>
          <button
            type="button"
            className="primary-button"
            onClick={() => void importPicked()}
            disabled={busy || picked.size === 0}
          >
            {busy ? <Spinner inline /> : tr('importSelected', picked.size)}
          </button>
        </>
      }
    >
      {error && <ErrorState code={error} />}
      {!error && !contacts && <Spinner />}
      {!error && contacts && (
        <>
          <input
            type="search"
            className="text-input"
            value={query}
            onChange={event => setQuery(event.target.value)}
            placeholder={tr('search')}
            aria-label={tr('search')}
          />
          {visible.length === 0
            ? <EmptyState title={tr('noGoogleContacts')} />
            : (
              <ul className="google-contacts">
                {visible.map(contact => (
                  <li key={contact.resourceName}>
                    <label className="check-label">
                      <input
                        type="checkbox"
                        checked={picked.has(contact.resourceName)}
                        onChange={() => toggle(contact.resourceName)}
                      />
                      <span className="google-contact-name">{contact.name}</span>
                      <bdi className="google-contact-phone">{contact.phone}</bdi>
                    </label>
                  </li>
                ))}
              </ul>
            )}
        </>
      )}
    </Modal>
  );
}
